/// <reference path="../../typings/tsd.d.ts" /> 

interface SignalR {

    serverHub: any;
}

module warsApp.services {
    
    export interface ISignalrService {
        getConnectionId(): string;
        stopConnection(notifyServerHubOfDisconnect: boolean): void;
        listen(controllerScope: angular.IScope): angular.IPromise<IListener>;
        broadcast(controllerScope: angular.IScope): angular.IPromise<IBroadcaster>;
    }

    export interface IBroadcaster {
        enterProcess(processId: string, addToGroup: boolean): void;
    }

    export interface IListener {
        onClientReceivedWarsNotification(callback: any): void;
        onSignalGroupOfProcessDataUpdate(callback: any): void;
    }
    
    export class SignalrService implements ISignalrService {
        
        private scope = this.$rootScope.$new();
        private qService: angular.IQService;
        private localStorageService: any;
        private hub = $.connection.serverHub;
        private tryingToReconnect = false;
        
        constructor(private $rootScope: angular.IRootScopeService, $q: angular.IQService, localStorageService: any) {
            var self = this;
            self.qService = $q;
            self.localStorageService = localStorageService;
        }

        //destroys the listener when the controller scope is destroyed
        private destroyListener(controllerScope, cb) {
            controllerScope.$on("$destroy", () => {
                cb();
            });
        }

        private initialize(): angular.IPromise<any> {
            var self = this;
            var d = self.qService.defer();
            if ($.connection.hub.state === SignalR.ConnectionState.Disconnected) {

                    //disconnected, so spin the connection
                self.startConnection()
                    .then(() => {
                        //connection started, so resolve
                        d.resolve();
                    },
                    (reason) => {
                        //connection not started due to not authenticated, so reject
                        console.error(reason);
                        d.reject();
                    });
            } else if ($.connection.hub.state === SignalR.ConnectionState.Connecting ||
                $.connection.hub.state === SignalR.ConnectionState.Reconnecting) {
                    $.connection.hub.stateChanged((state) => {
                        if (state.newState === SignalR.ConnectionState.Connected) {
                            //starting connection was started, so resolve
                            d.resolve();
                        } else {
                            //the state change was to a non-started state, so reject
                            console.error(`The new state of the connection was expected to be 'Connected' (1) but was ${state.newState}`);
                            d.reject();
                        }
                    });
            }
            if ($.connection.hub.state === SignalR.ConnectionState.Connected) {
                d.resolve();
            }
            return d.promise;
        }
        
        private startConnection(): angular.IPromise<any> {
            var self = this;
            var d = this.qService.defer();
            if ($.connection.hub.state === SignalR.ConnectionState.Connected) {
                d.resolve();
                return d.promise;
            }
            var authData = self.localStorageService.get("authorizationData");
            if (!authData) {
                d.reject("Authorization data is required to establish a connection");
                return d.promise;
            } 
                //signalr listener for signal logging configuration
                self.hub.client.signalConfigureLogging = (log: boolean) => {
                    $.connection.hub.logging = log;
                }
                //signalr listener for server side log actions
                self.hub.client.signalServerSideActionLogged = (source, message, level, url, sessionId) => {
                    if (source !== "NHibernate.SQL") {
                        var msg = source + " - " + message;
                        if (url != null) msg += `\nURL: ${url}`;
                        if (sessionId) msg += ` - Session ID: ${sessionId}`;
                        if (level === "FATAL") {
                            console.error(`Fatal !! - ${msg}`);
                        } else if (level === "ERROR") {
                            console.error(msg);
                        } else if (level === "WARN") {
                            console.warn(msg);
                        } else if (level === "INFO") {
                            console.info(msg);
                        } else if (level === "DEBUG") {
                            console.debug(msg);
                        }
                    } else {
                        var groupName = "NHibernate Log Entry";
                        if (url) groupName += ` - URL: ${url}`;
                        if (sessionId) groupName += ` - Session ID: ${sessionId}`;
                        console.groupCollapsed(groupName);
                        console.debug(message);
                        console.groupEnd();
                    }
                }
                //signalr listener for signal to log out of all client instances
                self.hub.client.signalClientLoggedOutToLogOutAllClientInstances = () => {
                    //emit the event on rootscope
                    self.scope.$emit("onSignalClientLoggedOutToLogOutAllClientInstances");
                }
                //signalr listener for signal when a client has received a wars notification    
                self.hub.client.signalClientReceivedWarsNotification = (notificationCount) => {
                    //emit the event on rootscope
                    self.scope.$emit("onSignalClientReceivedWarsNotification", notificationCount);
                }
                //signalr listener for signal when a client has updated field data on a process    
                self.hub.client.signalGroupOfProcessDataUpdate = (updatingUser: string, fieldDataToUpdate: any) => {
                    //emit the event on rootscope
                    console.debug(`SIGNALR NOTIFICATION ISSUE - Received process data update signal from ${updatingUser}`);
                    self.scope.$emit("onSignalGroupOfProcessDataUpdate", updatingUser, fieldDataToUpdate);
                }
                //add the bearer token to the querystring to hydrate the http context user on connection start
                $.connection.hub.qs = `bearer_token=${authData.token}`;
                //spin the connection
                $.connection.hub.start().done(() => {
                    $.connection.hub.reconnecting(() => {
                        self.tryingToReconnect = true;
                    });
                    $.connection.hub.reconnected(() => {
                        self.tryingToReconnect = false;
                    });
                    $.connection.hub.disconnected(() => {
                        if (self.tryingToReconnect) {
                            //IMPORTANT: if a transport error occurs and a connection is closed, a new connection ID will be generated on a new call to connection.hub.start().  This can cause errors or unexpected behavior
                            //for certain processes that register connections for groups (e.g. the enterProcess() broadcaster)
                            //temporary notification of a legitimate timeout / connection refused (i.e. server goes down) - we need to determine the best 'real' response to this condition, like logging the user out.
                            console.log("SignalR connection has timed out and cannot be restarted.  A new connection start will be attempted which if successful will result in a new connection ID for this client");
                        }
                    });
                    self.hub.server.onConfigureLogging();
                d.resolve();
                });
            return d.promise;
        }

        getConnectionId(): string {
            //if connected, return the connection ID, else return an empty string
            return $.connection.hub.state === SignalR.ConnectionState.Connected
                ? $.connection.hub.id
                : "";
        }

        stopConnection(notifyServerHubOfDisconnect:boolean): void {
            //if not disconnected, stop the connection
            if ($.connection.hub.state !== SignalR.ConnectionState.Disconnected) {
                $.connection.hub.stop(true, notifyServerHubOfDisconnect);
            }
        }
        
        //BROADCASTERS - implement custom broadcasters for events you would like to signal the server on
        //----------------------------------------------------------------------------------------------
        
        broadcast(controllerScope: angular.IScope): angular.IPromise<IBroadcaster> {
            var self = this;
            var d = self.qService.defer();
            self.initialize().then(() => {
                var result: IBroadcaster = {
                    //broadcaster method to register a client connection in a process group for future listening of process data changes
                    enterProcess(processId: string, addToGroup: boolean): void {
                        if (addToGroup) {
                            self.hub.server.onEnterProcess(processId);    
                        }
                        //remove the client from the process group when the client's scope is destroyed 
                        controllerScope.$on("$destroy", () => {
                            if (addToGroup) {
                                self.hub.server.onExitProcess(processId);    
                            }
                        });
                    }
                }
                d.resolve(result);
            });
            return d.promise;
        } 
        
        //LISTENERS - implement custom listeners to register callbacks on when notified by the server
        //NOTE: each one of these will need a matching SignalR listener with rootscope.$emit in the startConnection() method above
        //------------------------------------------------------------------------------------------------------------------------
        
        listen(controllerScope: angular.IScope): angular.IPromise<IListener> {
            var self = this;
            var d = self.qService.defer();
            self.initialize().then(() => {
                var result: IListener = {
                    //listener method for new notifications received
                    onClientReceivedWarsNotification(callback: any): void {
                        var cb = self.scope.$on("onSignalClientReceivedWarsNotification", (object, notificationCount) => {
                            controllerScope.$apply(callback(notificationCount));
                        });
                        self.destroyListener(controllerScope, cb);
                    }, //listener method for process data updates
                    onSignalGroupOfProcessDataUpdate(callback: any): void {
                        var cb = self.scope.$on("onSignalGroupOfProcessDataUpdate", (object, updatingUser, fieldDataToUpdate) => {
                            controllerScope.$apply(callback(updatingUser, fieldDataToUpdate));
                        });
                        self.destroyListener(controllerScope, cb);
                    }
                }
                d.resolve(result);
            });
            return d.promise;
        }
    }
}

warsApp.registerService("SignalrService", ["$rootScope", "$q", "localStorageService"]);